import { PPressSelectOption } from "@coeff/api";
import React, { useContext } from "react";
import styled from "styled-components";

import { EllipsizedText } from "../../common/EllipsizedText";
import { BorderedSelect as CoeffSelect, Option } from "../../common/Select";
import { PPressComponentProps, PpressContext, PPressContextParams } from "../context";

const StyledSelect = styled(CoeffSelect)<{ noMargin?: boolean }>`
  &.ant-select {
    width: 100%;
    min-width: 0;
    margin: 5px 0px;

    &.inline {
      width: auto;
    }

    &.small {
      width: auto;

      .ant-select-selector {
        height: 24px;
        border-width: 1px !important;

        .ant-select-selection-item {
          line-height: 22px;
        }

        .ant-select-arrow {
          top: 47%;
          right: 6px;
        }
      }

      &:hover {
        .ant-select-selector {
          border-width: 1px !important;
        }
      }

      &.ant-select-focused {
        .ant-select-selector {
          border-width: 1px !important;
        }
      }
    }

    ${({ noMargin }) => {
      if (noMargin) {
        return "margin: 0;";
      }

      return "";
    }}
  }
`;

type PPressSelectProps = {
  allowClear?: boolean;
  name: string;
  options: PPressSelectOption[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  mode?: "multiple" | undefined;
  defaultOpen?: boolean;
  onChange?: (value: unknown) => void;
  noMargin?: boolean;
  renderPrefixWhiteSpace?: boolean;
  dropdownRender?: (menu: React.ReactElement) => React.ReactElement;
  showSearch?: boolean;
} & PPressComponentProps;

export const Select = (props: PPressSelectProps) => {
  const { name, options = [], triggersOnChange, onChange } = props;

  const form_state = useContext(PpressContext) as PPressContextParams;

  const value = form_state?.ppress_params[name];

  const invalidValue =
    (typeof value === "string" || typeof value === "number") &&
    !options.some(option => option.value === value);

  return (
    <StyledSelect
      showSearch={props.showSearch}
      noMargin={props.noMargin}
      allowClear={props.allowClear}
      value={invalidValue ? undefined : value}
      optionFilterProp="title"
      onChange={async value => {
        if (onChange) {
          onChange(value);
          return;
        }
        if (props.updateFromServerOnChange) {
          await form_state.updateFormFromServer({
            ...form_state?.ppress_params,
            [name]: value,
          });
        } else {
          form_state.updatePPressParams({ [name]: value });

          if (triggersOnChange) {
            form_state.triggersOnChange(triggersOnChange);
          }
        }
      }}
      status={invalidValue ? "error" : undefined}
      disabled={props.disabled}
      className={"coeff__ppress_select " + props.className + (invalidValue ? " error" : "")}
      data-testid={`ppress-select-${name}`}
      mode={props.mode}
      placeholder={props.placeholder}
      maxTagCount={form_state?.ppress_params[name]?.length === options?.length ? 0 : 6}
      maxTagPlaceholder={omittedValues =>
        form_state?.ppress_params[name]?.length === options?.length
          ? "All Selected"
          : `+ ${omittedValues.length} more`
      }
      defaultOpen={props.defaultOpen}
      dropdownRender={props.dropdownRender}
      virtual={options?.length > 50}
    >
      {options?.map(({ value, label }, i) => (
        <Option key={i} title={label} value={value}>
          <EllipsizedText text={label} renderPrefixSpace={props.renderPrefixWhiteSpace} />
        </Option>
      ))}
    </StyledSelect>
  );
};
